import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, FileText, Send, CircleCheck, CircleOff, Banknote } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { CustomerCreateDialog } from "../../components/admin/CustomerCreateDialog";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY_ITEM = { name: "", spec: "", quantity: 1, unit: "", unit_price: 0 };
const EMPTY = {
  customer_id: "", project_id: "", title: "", issue_date: "", valid_until: "",
  items: [{ ...EMPTY_ITEM }], tax_rate: 10, notes: "", terms: "",
};

const STATUS_COLORS = { 下書き: "text-gray-400", 発行済: "text-accent", 成約: "text-green-400", 失注: "text-red-400" };

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminEstimates({ project, onChanged }) {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [registeringCustomer, setRegisteringCustomer] = useState(false);
  const customerSelectRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    api.get("/admin/estimates", { params: { ...(filterStatus ? { status: filterStatus } : {}), ...(project ? { project_id: project.id } : {}) } }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
  };
  const changed = () => { load(); onChanged?.(); };
  const openNew = () => {
    setForm({ ...EMPTY, ...(project ? { customer_id: project.customer_id, project_id: project.id, title: project.name } : {}), items: [{ ...EMPTY_ITEM }] });
    setEditing({});
  };

  useEffect(() => {
    api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, project?.id]);

  const totals = useMemo(() => {
    const subtotal = form.items.reduce((sum, it) => sum + Math.round((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)), 0);
    const tax = Math.floor((subtotal * (Number(form.tax_rate) || 0)) / 100);
    return { subtotal, tax, total: subtotal + tax };
  }, [form.items, form.tax_rate]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const customerCreated = (customer) => {
    setCustomers(rows => [...rows.filter(c => c.id !== customer.id), customer]);
    setForm(current => ({ ...current, customer_id: customer.id }));
    setRegisteringCustomer(false);
    toast.success(<span data-testid="estimate-customer-registration-success">顧客を登録し、見積書のお客様に選択しました</span>);
  };
  const setItem = (i, key) => (e) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [key]: e.target.value } : it)) }));

  const openEdit = (est) => {
    setForm({
      ...EMPTY,
      ...est,
      items: (est.items || []).map((it) => ({ name: it.name, spec: it.spec || "", quantity: it.quantity, unit: it.unit || "", unit_price: it.unit_price })),
    });
    setEditing(est);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      items: form.items.filter((it) => it.name.trim()).map((it) => ({ ...it, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 })),
      tax_rate: Number(form.tax_rate) || 0,
    };
    if (payload.items.length === 0) {
      toast.error("明細を1行以上入力してください");
      setSaving(false);
      return;
    }
    try {
      if (editing.id) await api.put(`/admin/estimates/${editing.id}`, payload);
      else await api.post("/admin/estimates", payload);
      toast.success("保存しました");
      setEditing(null);
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const issue = async (est) => {
    if (!window.confirm(`見積書 ${est.estimate_number} を発行しますか？\n発行後は内容を変更できません。`)) return;
    try {
      await api.post(`/admin/estimates/${est.id}/issue`);
      toast.success("発行しました");
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setStatus = async (est, status) => {
    if (!window.confirm(`見積書 ${est.estimate_number} を「${status}」にしますか？`)) return;
    try {
      await api.put(`/admin/estimates/${est.id}/status`, { status });
      toast.success(`「${status}」にしました`);
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (est) => {
    if (!window.confirm(`見積書 ${est.estimate_number} を削除しますか？`)) return;
    try {
      await api.delete(`/admin/estimates/${est.id}`);
      toast.success("削除しました");
      changed();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openPdf = (est) => window.open(`${api.defaults.baseURL}/admin/estimates/${est.id}/pdf`, "_blank");

  const createInvoice = async (est) => {
    if (!window.confirm(`見積書 ${est.estimate_number} から請求書（下書き）を作成しますか？`)) return;
    try {
      const r = await api.post(`/admin/estimates/${est.id}/create-invoice`);
      toast.success(`請求書 ${r.data.invoice_number} を作成しました（下書き）`);
      navigate("/admin/invoices");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const customerName = (id) => customers.find((c) => c.id === id)?.name || "—";
  const yen = (v) => `¥${Number(v || 0).toLocaleString()}`;

  return (
    <div data-testid="admin-estimates-page" className="business-documents doc-estimates">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-mincho text-lg font-bold text-white">見積書{project ? "・受注" : ""}</h2>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">ESTIMATES — 発行後は内容が固定されます</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="estimates-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての状態</option>
            <option>下書き</option><option>発行済</option><option>成約</option><option>失注</option>
          </select>
          <button data-testid="estimate-add-button" onClick={openNew} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            見積書を作成
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">見積番号</th>
              <th className="px-4 py-3">顧客</th>
              <th className="px-4 py-3">件名</th>
              <th className="px-4 py-3">合計（税込）</th>
              <th className="px-4 py-3">発行日</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((est) => (
              <tr key={est.id} data-testid={`estimate-row-${est.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{est.estimate_number}</td>
                <td className="px-4 py-3 text-xs text-gray-300">{customerName(est.customer_id)}</td>
                <td className="px-4 py-3 text-xs font-bold text-white">{est.title}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-white">{yen(est.total)}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-400">{est.issue_date}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-xs font-bold ${STATUS_COLORS[est.status]}`}>{est.status}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    {est.status === "下書き" && (
                      <>
                        <button data-testid={`estimate-edit-${est.id}`} onClick={() => openEdit(est)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="編集">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`estimate-issue-${est.id}`} onClick={() => issue(est)} className="border border-accent/50 p-2 text-accent transition-colors hover:bg-accent/10" title="発行">
                          <Send className="h-3.5 w-3.5" />
                        </button>
                        <button data-testid={`estimate-delete-${est.id}`} onClick={() => remove(est)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400" title="削除">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                    <button data-testid={`estimate-pdf-${est.id}`} onClick={() => openPdf(est)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent" title="PDF">
                      <FileText className="h-3.5 w-3.5" />
                    </button>
                    {!project && (est.status === "発行済" || est.status === "成約") && (
                      <button data-testid={`estimate-to-invoice-${est.id}`} onClick={() => createInvoice(est)} className="border border-green-500/40 p-2 text-green-400 transition-colors hover:bg-green-500/10" title="請求書を作成">
                        <Banknote className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {est.status === "発行済" && (
                      <>
                        <button data-testid={`estimate-won-${est.id}`} onClick={() => setStatus(est, "成約")} className="border border-green-500/40 p-2 text-green-400 transition-colors hover:bg-green-500/10" title="成約">
                          <CircleCheck className="h-3.5 w-3.5" /><span className="ml-1 text-xs">受注</span>
                        </button>
                        <button data-testid={`estimate-lost-${est.id}`} onClick={() => setStatus(est, "失注")} className="border border-red-500/40 p-2 text-red-400 transition-colors hover:bg-red-500/10" title="失注">
                          <CircleOff className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">見積書がありません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div style={registeringCustomer ? { display: "none" } : undefined} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="estimate-form" onSubmit={save} className="max-h-[90vh] w-full max-w-3xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? `見積書を編集（${editing.estimate_number}）` : "見積書を作成"}</h2>
              <button type="button" data-testid="estimate-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <Field label="顧客 *">
                  <select ref={customerSelectRef} data-testid="estimate-form-customer" disabled={!!project?.customer_id} required value={form.customer_id} onChange={set("customer_id")} className={inputCls}>
                    <option value="">選択してください</option>
                    {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                  </select>
                </Field>
                {!project?.customer_id && <button data-testid="estimate-register-customer" type="button" onClick={() => setRegisteringCustomer(true)} className="mt-2 inline-flex items-center gap-1.5 py-1.5 text-xs font-bold text-accent transition-colors hover:text-accent-hover"><Plus className="h-4 w-4" />顧客を新規登録</button>}
              </div>
              <Field label="関連する工事案件（任意）">
                <select data-testid="estimate-form-project" disabled={!!project} value={form.project_id} onChange={set("project_id")} className={inputCls}>
                  <option value="">—</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="件名 *"><input data-testid="estimate-form-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="造成工事一式" /></Field>
              <Field label="発行日（空欄で今日）"><input data-testid="estimate-form-issue-date" type="date" value={form.issue_date} onChange={set("issue_date")} className={inputCls} /></Field>
              <Field label="有効期限"><input data-testid="estimate-form-valid-until" type="date" value={form.valid_until} onChange={set("valid_until")} className={inputCls} /></Field>
              <Field label="消費税率">
                <select data-testid="estimate-form-tax-rate" value={form.tax_rate} onChange={set("tax_rate")} className={inputCls}>
                  <option value={10}>10%</option><option value={8}>8%</option><option value={0}>非課税</option>
                </select>
              </Field>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">明細</span>
                <button type="button" data-testid="estimate-item-add" onClick={() => setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }))} className="flex items-center gap-1 border border-line px-3 py-1.5 text-[11px] text-gray-300 transition-colors hover:border-accent hover:text-accent">
                  <Plus className="h-3.5 w-3.5" /> 行を追加
                </button>
              </div>
              <div className="mt-2 space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} data-testid={`estimate-item-${i}`} className="grid grid-cols-12 items-center gap-2 border border-line bg-ink p-2">
                    <input data-testid={`estimate-item-name-${i}`} value={it.name} onChange={setItem(i, "name")} placeholder="品名 *" className={`col-span-4 ${inputCls}`} />
                    <input data-testid={`estimate-item-spec-${i}`} value={it.spec} onChange={setItem(i, "spec")} placeholder="仕様" className={`col-span-3 ${inputCls}`} />
                    <input data-testid={`estimate-item-qty-${i}`} type="number" step="any" value={it.quantity} onChange={setItem(i, "quantity")} placeholder="数量" className={`col-span-1 ${inputCls}`} />
                    <input data-testid={`estimate-item-unit-${i}`} value={it.unit} onChange={setItem(i, "unit")} placeholder="単位" className={`col-span-1 ${inputCls}`} />
                    <input data-testid={`estimate-item-price-${i}`} type="number" value={it.unit_price} onChange={setItem(i, "unit_price")} placeholder="単価" className={`col-span-2 ${inputCls}`} />
                    <div className="col-span-1 flex items-center justify-between gap-1">
                      <span className="font-mono text-[10px] text-gray-400">{yen(Math.round((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)))}</span>
                      <button type="button" data-testid={`estimate-item-remove-${i}`} onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }))} disabled={form.items.length === 1} className="text-gray-600 transition-colors hover:text-red-400 disabled:opacity-30">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <div data-testid="estimate-form-totals" className="mt-3 space-y-1 border-t border-line pt-3 text-right text-sm">
                <p className="text-gray-400">小計: <span className="font-mono text-white">{yen(totals.subtotal)}</span></p>
                <p className="text-gray-400">消費税（{form.tax_rate}%）: <span className="font-mono text-white">{yen(totals.tax)}</span></p>
                <p className="text-base font-bold text-white">合計（税込）: <span className="font-mono text-accent">{yen(totals.total)}</span></p>
              </div>
            </div>

            <Field label="見積条件"><textarea data-testid="estimate-form-terms" rows={2} value={form.terms} onChange={set("terms")} className={`resize-y ${inputCls}`} placeholder="例：本見積の有効期限は発行日より30日間" /></Field>
            <Field label="備考"><textarea data-testid="estimate-form-notes" rows={2} value={form.notes} onChange={set("notes")} className={`resize-y ${inputCls}`} /></Field>

            <button data-testid="estimate-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する（下書き）"}
            </button>
          </form>
        </div>
      )}
      {registeringCustomer && <CustomerCreateDialog onClose={() => setRegisteringCustomer(false)} onCreated={customerCreated} onReturnFocus={() => customerSelectRef.current?.focus()} />}
    </div>
  );
}
